import { useUser } from "@/hooks/user";
import { formatCurrency } from "@/lib/utils";
import type { BBRouter } from "@/router";
import { ButtonLink } from "@bidbattles/bloks/components/ui/button";
import { Divider } from "@bidbattles/bloks/components/ui/divider";
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerTitle,
	DrawerTrigger,
} from "@bidbattles/bloks/components/ui/drawer";
import { Link } from "@bidbattles/bloks/components/ui/link";
import { useViteEnvironment } from "@bidbattles/bloks/hooks/dev";
import { BBBackendQueryOptions, type User } from "@bidbattles/bloks/lib/rapace";
import {
	Outlet,
	createFileRoute,
	redirect,
	useMatchRoute,
} from "@tanstack/react-router";
import { current } from "immer";
import { LucideLoader2 } from "lucide-react";
import { useDeferredValue, useState } from "react";
import { Menu01, Plus } from "untitledui-js/react";

export const Route = createFileRoute("/settings")({
	component: RouteComponent,
	async beforeLoad({ location, context }) {
		const user = await context.queryClient.ensureQueryData(
			BBBackendQueryOptions.me(),
		);
		if (!user) {
			throw redirect({ to: "/" });
		}

		if (
			location.pathname === "/settings" ||
			location.pathname === "/settings/"
		) {
			throw redirect({ to: "/settings/dashboard" });
		}
	},
});

type SettingsRoutes = Extract<
	keyof BBRouter["routesByPath"],
	`/settings/${string}`
>;

function RouteComponent() {
	const user = useUser();
	const matchRoute = useMatchRoute();
	// const [open, setOpen] = useState(false);
	const env = useViteEnvironment();

	const configuringNotifications = useDeferredValue(
		!!matchRoute({
			to: "/settings/notifications/preferences",
		}),
	);

	const routes: Array<{
		path: SettingsRoutes;
		title: string;
		role?: User["role"];
		action?: React.ReactNode;
		env?: "development";
		divider?: boolean;
	}> = [
		{
			path: "/settings/dashboard" as const,
			title: "Dashboard",
		},
		{
			path: "/settings/notifications" as const,
			title: "Notifications",
			divider: configuringNotifications,
			action: (
				<div className="flex gap-2 md:gap-4 text-gray-500 font-bold text-sm lg:text-3xl">
					<Link
						to="/settings/notifications"
						activeProps={
							configuringNotifications ? {} : { className: "text-gray-900" }
						}
						activeOptions={{
							includeSearch: true,
							explicitUndefined: true,
						}}
						search={{ status: undefined }}
					>
						All
					</Link>
					<Link
						to="/settings/notifications"
						activeProps={{ className: "text-gray-900" }}
						activeOptions={{ includeSearch: true }}
						search={{ status: "unread" }}
					>
						Unread
					</Link>
					<Link
						to="/settings/notifications/preferences"
						activeProps={{ className: "text-gray-900" }}
					>
						Settings
					</Link>
				</div>
			),
		},
		{
			path: "/settings/profile" as const,
			title: "Profile",
		},
		{
			path: "/settings/password" as const,
			title: "Change Password",
		},
		{
			path: "/settings/battles" as const,
			title: "Battles",
			divider: false,
			action: (
				<div className="flex gap-2 md:gap-4 text-gray-500 font-bold text-sm lg:text-3xl">
					<Link
						to="/settings/battles"
						activeProps={{ className: "text-gray-900" }}
						activeOptions={{ includeSearch: true, explicitUndefined: true }}
						search={{ winner: undefined }}
					>
						All
					</Link>
					<Link
						to="/settings/battles"
						activeProps={{ className: "text-gray-900" }}
						activeOptions={{ includeSearch: true }}
						search={{ winner: true }}
					>
						Won
					</Link>
					<Link
						to="/settings/battles"
						activeProps={{ className: "text-gray-900" }}
						activeOptions={{ includeSearch: true }}
						search={{ winner: false }}
					>
						Lost
					</Link>
				</div>
			),
		},
		{
			path: "/settings/earnings" as const,
			title: "Earnings",
			divider: false,
			action: (
				<div className="flex flex-col items-end text-end">
					<p className="font-medium text-xl lg:text-3xl">
						{formatCurrency(
							"LOYALTY_POINTS",
							user?.loyalty_points.balance ?? 0,
						)}
					</p>
					<p>Loyalty coins</p>
				</div>
			),
		},
		{
			path: "/settings/billing-history" as const,
			title: "Billing",
			divider: false,
		},
	].filter((route) => !route.role || route.role === user?.role);

	const _currentRouteConfig = routes.find((config) =>
		matchRoute({ to: config.path, fuzzy: true }),
	);
	const currentRoutePath = useDeferredValue(_currentRouteConfig?.path);
	const currentRouteConfig = routes.find(
		(config) => config.path === currentRoutePath,
	);

	return (
		<>
			<section className="flex flex-col items-center grow py-box">
				<div className="max-w-container px-box w-full lg:grid lg:grid-cols-12 lg:gap-12">
					<nav className="hidden lg:block lg:col-span-3 text-gray-500">
						<ul className="list-none flex flex-col gap-4">
							{routes.map((route) => (
								<li key={route.path}>
									<Link
										to={route.path}
										activeProps={{
											className: "text-gray-700",
										}}
									>
										{route.title}
									</Link>
								</li>
							))}
						</ul>
					</nav>
					<div className="flex flex-col grid-cols-12 md:col-span-9">
						<div className="flex items-center gap-4 py-2">
							<Drawer direction="left">
								<DrawerTrigger asChild>
									<button
										type="button"
										className="lg:hidden cursor-pointer text-2xl"
									>
										<Menu01 size="1em" stroke="currentColor" />
									</button>
								</DrawerTrigger>
								<DrawerContent className="left-0 bottom-0 top-0 right-[25%] h-full mt-0">
									<nav>
										<ul className="list-none flex flex-col gap-4">
											{routes.map((route) => (
												<li key={route.path}>
													<DrawerClose asChild>
														<Link
															to={route.path}
															activeProps={{
																className: "text-gray-700",
															}}
														>
															{route.title}
														</Link>
													</DrawerClose>
												</li>
											))}
										</ul>
									</nav>
								</DrawerContent>
							</Drawer>
							<div className="flex justify-between items-center w-full">
								<h1 className="font-medium text-xl lg:text-3xl">
									{currentRouteConfig?.title}
								</h1>
								{currentRouteConfig?.action}
							</div>
						</div>
						{currentRouteConfig?.divider !== false && (
							<Divider className="mt-4 mb-10" />
						)}
						<Outlet />
					</div>
				</div>
			</section>
		</>
	);
}
